import { GlobalAppWrapper, TopRightNav } from 'components';
import useCurrentUser from 'infrastructure/currentUser/useCurrentUser';
import { ErrorBoundary } from 'infrastructure/error';
import React, { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import logo from 'img/logo/storyteller-logo-white.svg';
import useGetMenu from 'slices/AccountV2/hooks/useGetMenu';
import Actions from 'slices/Actions/Loadable';
import Analytics from 'slices/Analytics/Loadable';
import Apps from 'slices/Apps/Loadable';
import EngagementUnit from 'slices/EngagementUnit/Loadable';
import Poll from 'slices/Poll/Loadable';
import Quiz from 'slices/Quiz/Loadable';
import Schedule from 'slices/Schedule/Loadable';
import Settings from 'slices/SettingsV2/Loadable';
import Tenant from 'slices/Tenant/Loadable';
import User from 'slices/User/Loadable';
import Categories from 'slices/Category/Loadable';
import Clips, { Analytics as ClipItemAnalytics } from 'slices/Clips/Loadable';
import Collections, {
  Analytics as CollectionAnalytics,
} from 'slices/Collections/Loadable';
import TextSets from 'slices/StudioAssets/TextSets/Loadable';
import Templates from 'slices/StudioAssets/Templates/Loadable';
import Animations from 'slices/StudioAssets/Animations/Loadable';
import Shapes from 'slices/StudioAssets/Shapes/Loadable';
import Fonts from 'slices/StudioAssets/Fonts/Loadable';
import Stickers from 'slices/StudioAssets/Stickers/Loadable';
import Filters from 'slices/StudioAssets/Filters/Loadable';
import MediaLibrary from 'slices/StudioAssets/MediaLibrary/Loadable';
import CustomColors from 'slices/StudioAssets/CustomColors/Loadable';
import AdsAnalytics from 'slices/AdsAnalytics';
import { ADS_ANALYTICS_ROUTE } from 'slices/AdsAnalytics/access';
import useAdsAnalyticsAccess from 'slices/AdsAnalytics/hooks/useAdsAnalyticsAccess';
import ClipsAnalytics from 'slices/ClipsAnalytics';
import CardsAnalytics from 'slices/CardsAnalytics';
import Recipes from 'slices/Recipes/Loadable';
import Story, { Analytics as StoryItemAnalytics } from 'slices/Story/Loadable';
import InterstitialAd, {
  Analytics as InterstitialAdItemAnalytics,
} from 'slices/InterstitialAdv2/Loadable';
import ContentBank from 'slices/ContentBank/Loadable';
import ContentSharing from 'slices/ContentSharing/Loadable';
import Locales from 'slices/Locales/Loadable';
import ContentSharingConfig from 'slices/ContentSharingConfig/Loadable';
import Sheets from 'slices/Sheets/Loadable';
import Cards, { Analytics as CardItemAnalytics } from 'slices/Cards/Loadable';
import CardCollections from 'slices/CardCollections/Loadable';
import CardThemes from 'slices/CardThemes/Loadable';
import Troubleshooting from 'slices/Troubleshooting/Loadable';
import SearchAlgorithms from 'slices/SearchAlgorithms/Loadable';
import AudienceManagement from 'slices/AudienceManagement/Loadable';
import isRouteCurrent from 'shared/helpers/isRouteCurrent';
import { CONTENT_SHARING_REQUESTS_COUNT } from 'slices/ContentSharing/types';
import CurrentTenantFontProvider from 'infrastructure/currentTenant/CurrentTenantFontContext';
import useFeatureFlags from 'infrastructure/features/useFeatureFlags';
import { PasswordManagerAutofillProvider } from 'infrastructure/form/PasswordManagerAutofillContext';
import {
  ANALYTICS_VIEWER_AGGREGATE_ROUTES,
  ANALYTICS_VIEWER_FALLBACK_ROUTE,
  isAnalyticsAlgorithmEditorPathAllowed,
  isAnalyticsViewerPathAllowed,
} from 'infrastructure/currentUser/analyticsViewerAccess';
import { canReadCmsSettings } from 'infrastructure/currentUser/cmsRoleAccess';

const CATEGORIES_ROUTE = '/categories';
const CATEGORY_TYPES_ROUTE = '/category-types';
const WSC_CONFIG_ROUTE = '/wsc';
const ALGORITHMS_ROUTE = '/algorithms';

interface SidebarChildLink {
  current: boolean;
  url: string;
  name: React.ReactNode;
  counterKey?: string;
}

interface SidebarLink {
  current: boolean;
  icon: React.ReactNode;
  url: string;
  name: React.ReactNode;
  displayName?: string;
  type: string;
  elementId: string;
  counterKey?: string;
  items: Array<SidebarChildLink>;
  cssClass?: string | null;
  customData?: { type?: string; iconPackage?: string };
  isDisabled?: boolean;
  isLeaf?: boolean;
  order?: number;
  target?: string | null;
}

const getStringValue = (value: React.ReactNode) =>
  typeof value === 'string' ? value : '';

const isCategoriesMenuItem = (link: SidebarLink) =>
  link.url === CATEGORIES_ROUTE ||
  link.elementId === 'MenuItem_Categories' ||
  getStringValue(link.name) === 'Categories' ||
  link.displayName === 'Categories' ||
  link.items.some(item => item.url === CATEGORIES_ROUTE);

const isCategoryTypesMenuItem = (link: SidebarLink | SidebarChildLink) =>
  link.url === CATEGORY_TYPES_ROUTE;

const isAdsAnalyticsMenuItem = (link: SidebarLink | SidebarChildLink) =>
  link.url === ADS_ANALYTICS_ROUTE;

const isWSCConfigMenuItem = (link: SidebarLink | SidebarChildLink) =>
  link.url === WSC_CONFIG_ROUTE;

const filterAdsAnalyticsNavigation = (links: Array<SidebarLink>) =>
  links
    .filter(link => !isAdsAnalyticsMenuItem(link))
    .map(link => ({
      ...link,
      items: link.items.filter(item => !isAdsAnalyticsMenuItem(item)),
    }));

const filterWSCConfigNavigation = (links: Array<SidebarLink>) =>
  links
    .filter(link => !isWSCConfigMenuItem(link))
    .map(link => ({
      ...link,
      items: link.items.filter(item => !isWSCConfigMenuItem(item)),
    }));

const filterAnalyticsViewerNavigation = (
  links: Array<SidebarLink>,
  includeAlgorithms: boolean
) => {
  const allowedUrls = new Set<string>(ANALYTICS_VIEWER_AGGREGATE_ROUTES);
  if (includeAlgorithms) {
    allowedUrls.add(ALGORITHMS_ROUTE);
  }

  return links
    .filter(
      link =>
        allowedUrls.has(link.url) ||
        link.displayName === 'Analytics' ||
        getStringValue(link.name) === 'Analytics'
    )
    .map(link => ({
      ...link,
      items: link.items.filter(item => allowedUrls.has(item.url)),
    }));
};

const getChildLink = (
  url: string,
  name: string,
  pathname: string
): SidebarChildLink => ({
  current: isRouteCurrent(url, pathname),
  name,
  url,
});

const getCategoriesMenuChildName = (item: SidebarChildLink) => {
  if (item.url === CATEGORIES_ROUTE) {
    return 'All Categories';
  }

  if (item.url === CATEGORY_TYPES_ROUTE) {
    return 'Types';
  }

  return item.name;
};

const normalizeCategoryTypeNavigation = (
  links: Array<SidebarLink>,
  canManageCategoryTypes: boolean,
  pathname: string
) => {
  const linksVisibleToCurrentUser = links
    .filter(link => canManageCategoryTypes || !isCategoryTypesMenuItem(link))
    .map(link => ({
      ...link,
      items: canManageCategoryTypes
        ? link.items
        : link.items.filter(item => !isCategoryTypesMenuItem(item)),
    }));

  if (!canManageCategoryTypes) {
    return linksVisibleToCurrentUser;
  }

  return linksVisibleToCurrentUser.map(link => {
    if (!isCategoriesMenuItem(link)) {
      return link;
    }

    const hasCategoriesChild = link.items.some(
      item => item.url === CATEGORIES_ROUTE
    );
    const hasCategoryTypesChild = link.items.some(
      item => item.url === CATEGORY_TYPES_ROUTE
    );
    const items = [
      ...(hasCategoriesChild
        ? []
        : [getChildLink(CATEGORIES_ROUTE, 'All Categories', pathname)]),
      ...link.items,
      ...(hasCategoryTypesChild
        ? []
        : [getChildLink(CATEGORY_TYPES_ROUTE, 'Types', pathname)]),
    ].map(item => ({
      ...item,
      current: isRouteCurrent(item.url, pathname),
      name: getCategoriesMenuChildName(item),
    }));

    return {
      ...link,
      current: items.some(item => item.current),
      customData: {
        ...link.customData,
        type: 'parent',
      },
      isLeaf: false,
      items,
      type: 'parent',
      url: '',
    };
  });
};

const DashboardContent = () => {
  const history = useHistory();
  const location = useLocation();
  const menuQuery = useGetMenu();
  const featureFlags = useFeatureFlags();
  const {
    currentUser,
    isSystemAdmin,
    isAdmin,
    hasCategoryManagementRestrictions,
    hasDeveloperPermissions,
    hasSuperAdminPermissions,
    hasGraphicsProducerPermissions,
    hasAnalyticsViewerPermissions,
    hasAnalyticsAlgorithmEditorPermissions,
  } = useCurrentUser();
  const tenantInfo = useMemo(() => currentUser?.tenantInfo, [currentUser]);
  const roles = useMemo(() => currentUser?.roles || [], [currentUser]);
  const shouldLoadTenantSettings = canReadCmsSettings(currentUser);
  const canAccessStorytellerCategories =
    !hasCategoryManagementRestrictions &&
    (tenantInfo?.categoriesProvider === 'storyteller' || !roles.length);
  const canManageCategoryTypes =
    Boolean(hasSuperAdminPermissions || isSystemAdmin) &&
    canAccessStorytellerCategories;
  const {
    canAccess: canAccessAdsAnalyticsRoute,
    isLoading: isAdsAnalyticsAccessLoading,
  } = useAdsAnalyticsAccess();
  const shouldMountAdsAnalyticsRoutes =
    isAdsAnalyticsAccessLoading || canAccessAdsAnalyticsRoute;
  const isRestrictedPathAllowed = hasAnalyticsAlgorithmEditorPermissions
    ? isAnalyticsAlgorithmEditorPathAllowed(location.pathname || '/')
    : isAnalyticsViewerPathAllowed(location.pathname || '/');

  const systemAdminTenant = window.location?.host?.includes('systemadmin');

  const sidebarLinks = useMemo(() => {
    const baseLinks =
      menuQuery.data?.map(menuItem => ({
        ...menuItem,
        items: menuItem.items.map(item => ({
          ...item,
          ...(item.elementId === 'MenuItem_Requests' && {
            counterKey: CONTENT_SHARING_REQUESTS_COUNT,
          }),
          current: isRouteCurrent(item.url, location.pathname),
        })),
        current: isRouteCurrent(menuItem.url, location.pathname),
      })) || [];

    let links = hasAnalyticsViewerPermissions
      ? filterAnalyticsViewerNavigation(
          [...baseLinks],
          Boolean(hasAnalyticsAlgorithmEditorPermissions)
        )
      : normalizeCategoryTypeNavigation(
          [...baseLinks],
          canManageCategoryTypes,
          location.pathname
        );

    if (hasCategoryManagementRestrictions) {
      links = links.filter(link => !isCategoriesMenuItem(link));
    }

    if (!hasAnalyticsViewerPermissions) {
      links = filterWSCConfigNavigation(links);
    }

    if (!isAdsAnalyticsAccessLoading && !canAccessAdsAnalyticsRoute) {
      links = filterAdsAnalyticsNavigation(links);
    }

    if (
      !hasAnalyticsViewerPermissions &&
      featureFlags?.troubleshooting === true
    ) {
      links.push({
        current: isRouteCurrent('/troubleshooting', location.pathname),
        icon: <FontAwesomeIcon icon={['fal', 'tools']} />,
        url: '/troubleshooting',
        name: 'Troubleshooting',
        displayName: 'Troubleshooting',
        type: '',
        elementId: 'MenuItem_Troubleshooting',
        items: [],
        cssClass: null,
        customData: { type: '', iconPackage: 'fal' },
        isDisabled: false,
        isLeaf: true,
        order: 999,
        target: null,
      } as any);
    }
    return links;
  }, [
    canAccessAdsAnalyticsRoute,
    isAdsAnalyticsAccessLoading,
    featureFlags?.troubleshooting,
    canManageCategoryTypes,
    hasCategoryManagementRestrictions,
    hasAnalyticsViewerPermissions,
    hasAnalyticsAlgorithmEditorPermissions,
    location.pathname,
    menuQuery.data,
  ]);

  useEffect(() => {
    if (!hasGraphicsProducerPermissions) return;

    const isRecipesPage = location.pathname.includes('/recipes');
    const isContentBankPage = location.pathname.includes('/content-bank');
    const isMediaLibrariesPage =
      location.pathname.includes('/media-libraries') ||
      location.pathname.includes('/mediaLibraries');

    if (!isRecipesPage && !isContentBankPage && !isMediaLibrariesPage) {
      history.push('/recipes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGraphicsProducerPermissions, location.pathname]);

  return (
    <CurrentTenantFontProvider loadTenantSettings={shouldLoadTenantSettings}>
      <GlobalAppWrapper
        logo={<img src={logo} alt="Storyteller" />}
        sidebarLinks={sidebarLinks}
        topbarContentRight={<TopRightNav />}
      >
        {isSystemAdmin && systemAdminTenant && (
          <ErrorBoundary>
            <Switch location={location}>
              <Route path="/tenant" component={Tenant} />
              <Route path="/user" component={User} />
              <Route
                path="/content-sharing-configuration"
                component={ContentSharingConfig}
              />
              <Route path="*">
                <Redirect to="/tenant" />
              </Route>
            </Switch>
          </ErrorBoundary>
        )}
        {(roles.length > 0 || isSystemAdmin) && !systemAdminTenant && (
          <ErrorBoundary>
            <>
              {hasAnalyticsViewerPermissions && isRestrictedPathAllowed && (
                <Switch location={location}>
                  <Route exact path="/analytics" component={Analytics} />
                  <Route
                    exact
                    path="/clips-analytics"
                    component={ClipsAnalytics}
                  />
                  <Route
                    exact
                    path="/cards-analytics"
                    component={CardsAnalytics}
                  />
                  {shouldMountAdsAnalyticsRoutes ? (
                    <Route
                      exact
                      path="/ads-analytics"
                      component={AdsAnalytics}
                    />
                  ) : null}
                  <Route
                    exact
                    path="/stories/:id/analytics"
                    component={StoryItemAnalytics}
                  />
                  <Route
                    exact
                    path="/clips/:id/analytics"
                    component={ClipItemAnalytics}
                  />
                  <Route
                    exact
                    path="/cards/:id/analytics"
                    component={CardItemAnalytics}
                  />
                  <Route
                    exact
                    path="/collections/:collectionId/analytics"
                    component={CollectionAnalytics}
                  />
                  {shouldMountAdsAnalyticsRoutes ? (
                    <Route
                      exact
                      path="/interstitialAd/:id/analytics"
                      component={InterstitialAdItemAnalytics}
                    />
                  ) : null}
                  {hasAnalyticsAlgorithmEditorPermissions && (
                    <Route
                      path={ALGORITHMS_ROUTE}
                      component={SearchAlgorithms}
                    />
                  )}
                  <Route path="*">
                    <Redirect to={ANALYTICS_VIEWER_FALLBACK_ROUTE} />
                  </Route>
                </Switch>
              )}
              {hasAnalyticsViewerPermissions && !isRestrictedPathAllowed && (
                <Redirect to={ANALYTICS_VIEWER_FALLBACK_ROUTE} />
              )}
              {!hasAnalyticsViewerPermissions && (
                <Switch location={location}>
                  <Route path="/schedule" component={Schedule} />
                  {isAdmin && <Route path="/user" component={User} />}
                  {(hasDeveloperPermissions ||
                    hasSuperAdminPermissions ||
                    isSystemAdmin) && <Route path="/apps" component={Apps} />}
                  {isAdmin && (
                    <Route path="/algorithms" component={SearchAlgorithms} />
                  )}
                  {isAdmin && (
                    <Route path="/audiences" component={AudienceManagement} />
                  )}
                  <Route path="/stories" component={Story} />
                  <Route path="/analytics" component={Analytics} />
                  <Route path="/ads-analytics" component={AdsAnalytics} />
                  <Route path="/clips-analytics" component={ClipsAnalytics} />
                  <Route path="/cards-analytics" component={CardsAnalytics} />
                  <Route path="/adexport" component={InterstitialAd} />
                  <Route path="/interstitialAd" component={InterstitialAd} />
                  <Route path="/engagement-unit" component={EngagementUnit} />
                  <Route path="/poll" component={Poll} />
                  <Route path="/quiz" component={Quiz} />
                  <Route path="/actions" component={Actions} />
                  <Route path="/clips" component={Clips} />
                  <Route path="/collections" component={Collections} />
                  <Route path="/recipes" component={Recipes} />
                  <Route path="/content-bank" component={ContentBank} />
                  <Route path="/media-libraries" component={MediaLibrary} />
                  {isSystemAdmin && (
                    <Route path="/mediaLibraries" component={MediaLibrary} />
                  )}
                  <Route path="/content-sharing" component={ContentSharing} />
                  <Route path="/locales" component={Locales} />
                  <Route path="/sheets" component={Sheets} />
                  <Route path="/card-collections" component={CardCollections} />
                  {isSystemAdmin && (
                    <Route path="/card-themes" component={CardThemes} />
                  )}
                  <Route path="/cards" component={Cards} />
                  <Route path="/troubleshooting" component={Troubleshooting} />

                  {isSystemAdmin && (
                    <Route path="/placementSettings" component={Settings} />
                  )}
                  {isSystemAdmin && (
                    <Route path="/sharingSettings" component={Settings} />
                  )}
                  {isSystemAdmin && (
                    <Route path="/loginMethods" component={Settings} />
                  )}
                  {isSystemAdmin && (
                    <Route path="/adSettings" component={Settings} />
                  )}
                  {isSystemAdmin && (
                    <Route path="/textSets">
                      <TextSets />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/templates">
                      <Templates />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/animations">
                      <Animations />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/shapes">
                      <Shapes />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/fonts">
                      <Fonts />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/stickers">
                      <Stickers />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/filters">
                      <Filters />
                    </Route>
                  )}
                  {isSystemAdmin && (
                    <Route path="/customColors">
                      <CustomColors />
                    </Route>
                  )}
                  {canAccessStorytellerCategories && (
                    <Route path="/categories" component={Categories} />
                  )}
                  {canManageCategoryTypes && (
                    <Route path="/category-types" component={Categories} />
                  )}
                  <Route path="*">
                    <Redirect to="/stories" />
                  </Route>
                </Switch>
              )}
            </>
          </ErrorBoundary>
        )}
      </GlobalAppWrapper>
    </CurrentTenantFontProvider>
  );
};

const Dashboard = () => (
  <PasswordManagerAutofillProvider mode="ignore">
    <DashboardContent />
  </PasswordManagerAutofillProvider>
);

export default Dashboard;
